import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  Typography,
  Grid,
  TextField,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import JSZip from 'jszip';
import api from '../utils/api';
import TableToolbar from '../components/TableToolbar';
import { useSidebar } from '../components/Layout';
import InfoIcon from '@mui/icons-material/Info';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const QuickBooksInvoiceData = () => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest'
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const { fullTableMode } = useSidebar();

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Set default dates if not selected
      const defaultStartDate = new Date();
      defaultStartDate.setMonth(defaultStartDate.getMonth() - 6);
      
      const startDateToUse = startDate || defaultStartDate;
      const endDateToUse = endDate || new Date();
      
      // Format dates in yyyy-MM-dd format for the API
      const formattedStartDate = format(startDateToUse, 'yyyy-MM-dd');
      const formattedEndDate = format(endDateToUse, 'yyyy-MM-dd');
      
      params.append('start_date', formattedStartDate);
      params.append('end_date', formattedEndDate);

      console.log('Fetching invoices with params:', Object.fromEntries(params));
      const response = await api.get(`/v1/quickbooks?${params}`);
      console.log('Raw response:', response);
      
      if (response.data && response.data.data) {
        console.log('Setting invoices:', response.data.data);
        // Keep dates in their original format from the backend
        setInvoices(response.data.data);
        setFilteredInvoices(response.data.data);
        
        // Set the dates in the UI if they weren't set
        if (!startDate) setStartDate(startDateToUse);
        if (!endDate) setEndDate(endDateToUse);
      } else {
        console.error('Invalid response format:', response.data);
        enqueueSnackbar('Invalid data format received from server', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch invoice data';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Sort invoices by date
  const sortInvoicesByDate = (invoiceList) => {
    return [...invoiceList].sort((a, b) => {
      const dateA = a['invoice_date'] ? new Date(a['invoice_date']) : new Date(0);
      const dateB = b['invoice_date'] ? new Date(b['invoice_date']) : new Date(0);
      
      if (sortOrder === 'newest') {
        return dateB - dateA; // Newest first
      } else {
        return dateA - dateB; // Oldest first
      }
    });
  };

  // Filter invoices based on search term and sort
  useEffect(() => {
    let filtered = invoices;
    
    if (searchTerm.trim() !== '') {
      filtered = invoices.filter(invoice => {
        // Search across multiple fields
        return (
          (invoice['invoice_no'] && invoice['invoice_no'].toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
          (invoice['customers'] && invoice['customers'].toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
          (invoice['memo'] && invoice['memo'].toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
          (invoice['item_product_service)'] && invoice['item_product_service)'].toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
          (invoice['item_description'] && invoice['item_description'].toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
          (invoice["client_company_email"] && invoice["client_company_email"].toString().toLowerCase().includes(searchTerm.toLowerCase()))
        );
      });
    }
    
    // Sort the filtered invoices
    const sorted = sortInvoicesByDate(filtered);
    setFilteredInvoices(sorted);
    setPage(0); // Reset to first page when search term or sort order changes
  }, [searchTerm, invoices, sortOrder]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleExport = async () => {
    try {
      console.log("filteredInvoices");
      console.log(filteredInvoices);

      setLoading(true);
      


      // Format the data to match PHP structure
      const formattedData = filteredInvoices.map((invoice, index) => {
        // Use dynamic invoice_no from invoice data
        const count = invoice['invoice_no'];
        
        // Safely parse the visit date
        let visitDate = null;
        try {
          if (invoice['invoice_date']) {
            visitDate = new Date(invoice['invoice_date']);
            if (isNaN(visitDate.getTime())) {
              visitDate = null;
            }
          }
        } catch (e) {
          console.error('Error parsing visit date:', e);
          visitDate = null;
        }

        // Calculate due date
        let dueDate = null;
        const due_days = parseInt(invoice['terms']?.replace('Net ', '') || '0');
        if (visitDate && due_days > 0) {
          dueDate = new Date(visitDate);
          dueDate.setDate(dueDate.getDate() + due_days);
        } else if (visitDate) {
          dueDate = visitDate;
        }
        
        // Format dates
        const formattedVisitDate = visitDate ? format(visitDate, 'yyyy-MM-dd') : '';
        const formattedDueDate = dueDate ? format(dueDate, 'yyyy-MM-dd') : '';
        
        // Extract data from the invoice object
        const clientName = invoice['customers'] || '';
        const itemDescription = invoice['item_description'] || '';
        const itemQuantity = invoice['item_quantity'] || '';
        const itemRate = invoice['item_rate'] || '';
        const itemAmount = invoice['item_amount'] || '';
        const serviceDate = invoice['service_date'] || '';
        const clientEmail = invoice['client_company_email'] || '';
        
        // Parse item data with type checking
        const items = typeof invoice['item_product_service'] === 'string' 
          ? invoice['item_product_service'].split('\n') 
          : ['Field Service'];
        
        // Handle itemQuantity based on its type
        let quantities = ['0'];
        if (typeof itemQuantity === 'string') {
          quantities = itemQuantity.split('\n');
        } else if (typeof itemQuantity === 'number') {
          quantities = [itemQuantity.toString()];
        }
        
        // Handle itemRate based on its type
        let rates = ['0'];
        if (typeof itemRate === 'string') {
          rates = itemRate.split('\n');
        } else if (typeof itemRate === 'number') {
          rates = [itemRate.toString()];
        }
        
        // Calculate amounts
        let totalAmount = 0;
        if (typeof itemAmount === 'string') {
          totalAmount = parseFloat(itemAmount.replace(/[^0-9.-]+/g, '')) || 0;
        } else if (typeof itemAmount === 'number') {
          totalAmount = itemAmount;
        }

        return {
          id: count,
          client_company_name: clientName,
          visit_date: formattedVisitDate,
          due_date: formattedDueDate,
          terms: invoice['Terms'] || 'Due on receipt',
          location: invoice['Location'] || '-',
          memo: invoice['Memo'] || '-',
          items: items,
          item_desc: itemDescription,
          item_quantity: quantities,
          item_rate: rates,
          item_amount: totalAmount,
          service_date: serviceDate,
          client_email: clientEmail
        };
      });

      // Create ZIP file
      const zip = new JSZip();
      const BATCH_SIZE = 100;
      const timestamp = format(new Date(), 'yyyyMMdd_HHmmss');

      // Process data in batches of 100
      for (let i = 0; i < formattedData.length; i += BATCH_SIZE) {
        const batch = formattedData.slice(i, i + BATCH_SIZE);
        const batchNumber = Math.floor(i / BATCH_SIZE) + 1;
        
        // Create CSV content for this batch
        const csvHeader = [
          '*InvoiceNO', '*Customer', '*InvoiceDate', '*DueDate', 'Terms',
          'Location', 'Memo', 'Item(Product/Service)', 'Item Description',
          'Item Quantity', 'Item Rate', '*Item Amount', 'Service Date', 
          "Client's email address"
        ];

        let csvContent = csvHeader.join(',') + '\n';

        // Add data rows
        batch.forEach(row => {
          // Process each item in the row
          for (let j = 0; j < row.items.length; j++) {
            const itemType = row.items[j];
            const quantity = parseFloat(row.item_quantity[j]) || 0;
            const rate = parseFloat(row.item_rate[j]) || 0;
            const amount = quantity * rate;
            
            // For travel time rows, use a different description
            let description = row.item_desc;
            if (itemType === 'TravelTime' && j > 0) {
              // Extract postal codes from the description if available
              const postalMatch = row.item_desc.match(/([A-Z0-9\s]+)\s+TO\s+([A-Z0-9\s]+)/);
              if (postalMatch) {
                description = `Travel time ${postalMatch[1].trim()} to ${postalMatch[2].trim()}`;
              } else {
                description = `Travel time`;
              }
            }
            
            // Create CSV row
            const csvRow = [
              row.id,
              j === 0 ? row.client_company_name : '', // Only include customer name in first row
              j === 0 ? row.visit_date : '', // Only include invoice date in first row
              j === 0 ? row.due_date : '', // Only include due date in first row
              j === 0 ? row.terms : '', // Only include terms in first row
              j === 0 ? row.location : '', // Only include location in first row
              '-', // Always show "-" in Memo column
              itemType,
              description,
              quantity.toFixed(2),
              rate.toFixed(2),
              amount.toFixed(2),
              j === 0 ? row.service_date : '', // Only include service date in first row
              j === 0 ? row.client_email : '' // Only include email in first row
            ].map(value => {
              if (value === null || value === undefined) return '""';
              return `"${String(value).replace(/"/g, '""')}"`;
            }).join(',');
            
            csvContent += csvRow + '\n';
          }
        });

        // Add batch file to ZIP with PHP-style naming
        zip.file(`csv_exports/quick_book${batchNumber}_file${batchNumber}.csv`, csvContent);
      }

      // Generate and download ZIP file with PHP-style naming
      const zipContent = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipContent);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quick_book_csv.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar(`Exported ${formattedData.length} invoices in ${Math.ceil(formattedData.length / BATCH_SIZE)} batches`, { variant: 'success' });
    } catch (error) {
      console.error('Error exporting data:', error);
      enqueueSnackbar('Failed to export data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Format currency values
  const formatCurrency = (value) => {
    if (typeof value === 'number') {
      return `$${Number(value).toFixed(2)}`;
    } else if (typeof value === 'string' && value.trim() !== '') {
      const numValue = parseFloat(value.replace(/[^0-9.-]+/g, ''));
      if (!isNaN(numValue)) {
        return `$${numValue.toFixed(2)}`;
      }
    }
    return value;
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ 
      p: { xs: 0, sm: 2, md: 3 },
      width: '100%',
      backgroundColor: '#f5f5f5',
      minHeight: '100vh',
      margin: 0,
    }}>
      <TableToolbar title="QuickBooks Invoice Data" />
      
      <Box sx={{ 
        marginBottom: { xs: 2, sm: 4 },
        display: 'flex', 
        justifyContent: 'flex-end', 
        alignItems: 'center', 
        px: { xs: 2, sm: 2 },
        py: { xs: 1.5, sm: 2 },
        mx: { xs: 0, sm: 0 },
        backgroundColor: 'white',
        borderRadius: { xs: 0, sm: 3 },
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0'
      }}>
        <Card sx={{ mb: 3, boxShadow: 'none', borderRadius: 0, backgroundColor: 'transparent' }}>
          <CardContent sx={{ p: 0 }}>
          
            <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
            <Chip 
              label={`${filteredInvoices.length} Invoices`} 
              sx={{ 
                fontWeight: 600,
                backgroundColor: '#fdd415',
                color: '#4e0159'
              }}
            />
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: { xs: 2, md: 3 }, 
            mb: { xs: 2, sm: 3 }
          }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              gap: { xs: 1.5, sm: 2 }, 
              flex: 1,
              width: '100%'
            }}>
              <Box sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#555' }}>
                  Start Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        InputProps: {
                          startAdornment: <CalendarTodayIcon sx={{ color: '#4e0159', mr: 1 }} />,
                        },
                        sx: {
                          backgroundColor: 'white',
                          borderRadius: '4px',
                          '& .MuiOutlinedInput-root': {
                            height: '40px',
                            '& fieldset': {
                              borderColor: '#ddd',
                              borderRadius: '4px',
                            },
                            '&:hover fieldset': {
                              borderColor: '#4e0159',
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            paddingLeft: '16px'
                          }
                        }
                      },
                      day: (ownerState) => ({
                        sx: {
                          ...(ownerState.selected && {
                            backgroundColor: '#4e0159',
                            color: '#ffffff',
                            '&:hover': {
                              backgroundColor: '#4e0159'
                            }
                          }),
                          ...(ownerState.today && !ownerState.selected && {
                            backgroundColor: '#4e0159',
                            color: '#ffffff',
                            '&:hover': {
                              backgroundColor: '#4e0159'
                            }
                          })
                        }
                      })
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <Box sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#555' }}>
                  End Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        InputProps: {
                          startAdornment: <CalendarTodayIcon sx={{ color: '#4e0159', mr: 1 }} />,
                        },
                        sx: {
                          backgroundColor: 'white',
                          borderRadius: '4px',
                          '& .MuiOutlinedInput-root': {
                            height: '40px',
                            '& fieldset': {
                              borderColor: '#ddd',
                              borderRadius: '4px',
                            },
                            '&:hover fieldset': {
                              borderColor: '#4e0159',
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            paddingLeft: '16px'
                          }
                        }
                      },
                      day: (ownerState) => ({
                        sx: {
                          ...(ownerState.selected && {
                            backgroundColor: '#4e0159',
                            color: '#ffffff',
                            '&:hover': {
                              backgroundColor: '#4e0159'
                            }
                          }),
                          ...(ownerState.today && !ownerState.selected && {
                            backgroundColor: '#4e0159',
                            color: '#ffffff',
                            '&:hover': {
                              backgroundColor: '#4e0159'
                            }
                          })
                        }
                      })
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'flex-end' },
                gap: { xs: 1.5, sm: 1 },
                width: { xs: '100%', sm: 'auto' }
              }}>
                <FormControl 
                  size="small" 
                  sx={{ 
                    minWidth: { xs: '100%', sm: '150px' },
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      '& fieldset': {
                        borderColor: '#ddd',
                        borderRadius: '4px',
                      },
                      '&:hover fieldset': {
                        borderColor: '#4e0159',
                      }
                    }
                  }}
                >
                  <InputLabel sx={{ color: '#555' }}>Sort By</InputLabel>
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    label="Sort By"
                    sx={{
                      height: '40px',
                      '& .MuiSelect-select': {
                        paddingLeft: '14px'
                      }
                    }}
                  >
                    <MenuItem value="newest">Newest First</MenuItem>
                    <MenuItem value="oldest">Oldest First</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  startIcon={<FilterListIcon />}
                  fullWidth={isMobile}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    background: '#4e0159',
                    boxShadow: '0 4px 16px rgba(78, 1, 89, 0.3)',
                    '&:hover': {
                      background: '#4e0159',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 32px rgba(78, 1, 89, 0.4)'
                    }
                  }}
                  onClick={fetchInvoices}
                >
                  Filter
                </Button>

                <TextField
                  variant="outlined"
                  placeholder="Search invoices..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  size="small"
                  fullWidth={isMobile}
                  sx={{ 
                    width: { xs: '100%', sm: '200px' },
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      '& fieldset': {
                        borderColor: '#ddd',
                        borderRadius: '4px',
                      },
                      '&:hover fieldset': {
                      borderColor: '#4e0159',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: '#4e0159', mr: 1 }} />,
                  }}
                />

                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExport}
                  disabled={loading || invoices.length === 0}
                  fullWidth={isMobile}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    background: 'linear-gradient(135deg, #ff9800, #ffb74d)',
                    boxShadow: '0 4px 16px rgba(255, 152, 0, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #f57c00, #ff9800)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 32px rgba(255, 152, 0, 0.4)'
                    }
                  }}
                >
                  Export
                </Button>
                 
                <Tooltip title="Export Instructions">
                  <IconButton 
                    onClick={() => window.open(`${process.env.REACT_APP_WEB_URL}/QBO_online_export_instructions.pdf`, '_blank', 'noopener,noreferrer')}
                      sx={{ 
                      height: '40px',
                      width: '40px',
                      backgroundColor: 'white',
                      alignSelf: { xs: 'flex-start', sm: 'auto' },
                      '&:hover': {
                        backgroundColor: 'white',
                        opacity: 0.9
                      }
                    }}
                  >
                    <InfoIcon sx={{ color: '#ccc' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
      </Box>

      <Paper sx={{ 
        width: '100%', 
        mx: { xs: 0, sm: 0 },
        borderRadius: { xs: 0, sm: 3 },
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        border: { xs: 'none', sm: '1px solid #e0e0e0' },
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: '0 16px 64px rgba(0,0,0,0.15)',
          transform: { xs: 'none', sm: 'translateY(-4px)' }
        }
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress size={40} thickness={4} />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ 
              width: '100%',
              margin: 0,
              padding: 0,
              overflowX: { xs: 'auto', sm: 'hidden' }
            }}>
              <Table stickyHeader sx={{
                width: '100%',
                margin: 0
              }}>
                <TableHead
                  sx={{
                    '& .MuiTableCell-head': {
                      backgroundColor: '#4e0159',
                      color: '#ffffff'
                    }
                  }}
                >
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 2,
                      paddingRight: 1
                    }}>Invoice No</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Customers</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Invoice Date</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Due Date</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Terms</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Location</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Memo</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Item(Product/Service)</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Item Description</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Item Quantity</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Item Rate</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Item Amount</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 1
                    }}>Service Date</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      borderBottom: 'none',
                      paddingLeft: 1,
                      paddingRight: 2
                    }}>Client Company's Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInvoices && filteredInvoices.length > 0 ? (
                    filteredInvoices
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((invoice, index) => (
                        <TableRow 
                          key={index}
                          sx={{ 
                            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
                            '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }
                          }}
                        >
                    <TableCell sx={{ fontWeight: 500, paddingLeft: 2, paddingRight: 1 }}>{invoice['invoice_no']}</TableCell>
                          <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{invoice['customers']}</TableCell>
                          <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{invoice['invoice_date']}</TableCell>
                          <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{invoice['due_date']}</TableCell>
                          <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{invoice['terms']}</TableCell>
                          <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{invoice['location']}</TableCell>
                          <TableCell sx={{ paddingLeft: 1, paddingRight: 1, whiteSpace: 'pre-line' }}>{invoice['memo'] || '-'}</TableCell>
                          <TableCell sx={{ paddingLeft: 1, paddingRight: 1, whiteSpace: 'pre-line' }}>{invoice['item_product_service']}</TableCell>
                          <TableCell sx={{ paddingLeft: 1, paddingRight: 1, whiteSpace: 'pre-line' }}>{invoice['item_description']}</TableCell>
                          <TableCell sx={{ paddingLeft: 1, paddingRight: 1, whiteSpace: 'pre-line' }}>{invoice['item_quantity']}</TableCell>
                          <TableCell sx={{ paddingLeft: 1, paddingRight: 1, whiteSpace: 'pre-line' }}>{invoice['item_rate']}</TableCell>
                          <TableCell sx={{ fontWeight: 500, color: '#4e0159', paddingLeft: 1, paddingRight: 1 }}>{invoice['item_amount']}</TableCell>
                          <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{invoice['service_date']}</TableCell>
                          <TableCell sx={{ paddingLeft: 1, paddingRight: 2 }}>{invoice['client_company_email']}</TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          {searchTerm ? 'No matching invoices found' : 'No invoice data available'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredInvoices.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: '1px solid #e0e0e0',
                '.MuiTablePagination-select': {
                  borderRadius: '4px',
                  '&:focus': {
                    borderRadius: '4px',
                  }
                }
              }}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default QuickBooksInvoiceData;

<style jsx>{`
  .tooltip1:hover .tooltiptext {
    display: block !important;
  }
`}</style> 